import 'katex/dist/katex.min.css';
import PropTypes from 'prop-types';
import rehypeKatex from 'rehype-katex';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

/**
 * Renders agent response text as markdown. Internal links (starting with /)
 * are handled via the onNavigate callback so they navigate within the SPA
 * rather than doing a full-page reload. External links open in a new tab.
 */
function AgentMarkdown({ content, onNavigate, openLinksInNewTab = false }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        a: ({ href, children }) => {
          if (href?.startsWith('/') && !openLinksInNewTab) {
            return (
              <a
                href={href}
                className="text-primary underline decoration-primary/40 hover:decoration-primary"
                onClick={e => { e.preventDefault(); onNavigate?.(href); }}
              >
                {children}
              </a>
            );
          }
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline decoration-primary/40 hover:decoration-primary">
              {children}
            </a>
          );
        },
        code: ({ children }) => (
          <code className="bg-slate-200 px-1 rounded text-xs font-mono">{children}</code>
        ),
        pre: ({ children }) => (
          <pre className="bg-slate-100 rounded p-2 text-xs font-mono overflow-x-auto my-1 whitespace-pre-wrap">{children}</pre>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-1">
            <table className="text-xs border-collapse w-full">{children}</table>
          </div>
        ),
        th: ({ children }) => <th className="border border-slate-300 px-2 py-1 bg-slate-100 font-semibold text-left">{children}</th>,
        td: ({ children }) => <td className="border border-slate-300 px-2 py-1">{children}</td>,
        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
        ul: ({ children }) => <ul className="list-disc list-outside pl-4 mb-1 space-y-0.5">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-outside pl-4 mb-1 space-y-0.5">{children}</ol>,
        h1: ({ children }) => <h1 className="font-semibold text-sm mt-2 mb-1">{children}</h1>,
        h2: ({ children }) => <h2 className="font-semibold text-sm mt-2 mb-1">{children}</h2>,
        h3: ({ children }) => <h3 className="font-medium text-sm mt-1.5 mb-0.5">{children}</h3>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

AgentMarkdown.propTypes = {
  content: PropTypes.string.isRequired,
  onNavigate: PropTypes.func,
};

export default AgentMarkdown;
